import { InspectionType, InspectionStatusType } from '@apply/constants';
import { formatDateTime } from '@apply/ui';
import { onPercentRender, onDownloadClick } from '../functions/functions';
import { onStatusRender } from './components/OnStatusRender';
import { useInspectionsModalStore, canOrderDrawInspection } from '../store/useInspectionsModalStore';
import { useInspectionsStateQuery } from '../queries/inspectionsQueries';

interface InspectionDetailsProps {
    onRefreshStatus: () => void;
    onResetError: () => void;
    onLinkToDraw: () => Promise<void>;
}

const InspectionDetails = ({ onRefreshStatus, onResetError, onLinkToDraw }: InspectionDetailsProps) => {
    const { currentInspection, drawId, setDrawId } = useInspectionsModalStore();
    const { data: inspectionsData } = useInspectionsStateQuery();

    const inspection = currentInspection!;
    const draw =
        inspection.inspectionTypeId === InspectionType.Draw
            ? inspectionsData?.draws.find((d) => d.id === inspection.drawId)
            : null;

    return (
        <div
            className='row'
            style={{ border: '1px solid silver', borderRadius: '5px', margin: '0 15px' }}
        >
            <div
                className='columns small-12 large-12'
                style={{ paddingBottom: '3px' }}
            >
                <h2
                    className='decorated'
                    style={{ marginTop: '10px' }}
                >
                    Inspection Details
                </h2>
                <table>
                    <tbody>
                        <tr style={{ height: '36px' }}>
                            <td style={{ width: '140px' }}>Type:</td>
                            <td>
                                <b>{inspection.inspectionTypeId === InspectionType.Status ? 'Status' : 'Draw'}</b>
                            </td>
                        </tr>

                        <tr>
                            <td>Status:</td>
                            <td>
                                <b>{onStatusRender({ value: inspection.statusId })}</b>
                            </td>
                        </tr>

                        {inspection.statusId === InspectionStatusType.Error && (
                            <tr>
                                <td>Error Details:</td>
                                <td>
                                    <b>
                                        <span style={{ color: 'red' }}>{inspection.error}</span>;{}
                                    </b>
                                    <br />
                                    <button
                                        className='button hollow'
                                        type='button'
                                        title='Attempt to Reset Error and poll Granite for latest status'
                                        onClick={onResetError}
                                        style={{ padding: '6px', margin: '0' }}
                                    >
                                        Reset Error
                                    </button>
                                </td>
                            </tr>
                        )}

                        <tr>
                            <td style={{ width: '120px' }}>Draw #:</td>
                            <td>
                                {inspection.inspectionTypeId === InspectionType.Draw && draw && (
                                    <b>
                                        {draw.number} ({draw.drawType})
                                    </b>
                                )}
                                {inspection.inspectionTypeId === InspectionType.Status && (
                                    <>
                                        <select
                                            id='drawNumber'
                                            value={drawId ?? ''}
                                            onChange={(e) => setDrawId(e.target.value)}
                                            style={{ width: '150px' }}
                                            disabled={inspection.statusId !== InspectionStatusType.InspectionDelivered}
                                        >
                                            <option value=''>N/A</option>
                                            {inspectionsData!.draws.map((d) => (
                                                <option
                                                    key={'draw' + d.id}
                                                    value={d.id}
                                                    disabled={!canOrderDrawInspection(inspectionsData ?? null, d.id)}
                                                >
                                                    {d.number} - {d.drawType}
                                                </option>
                                            ))}
                                        </select>
                                        &nbsp;
                                        <button
                                            className='button hollow'
                                            type='button'
                                            title='Link to Draw'
                                            onClick={onLinkToDraw}
                                            style={{ padding: '6px', margin: '0' }}
                                            disabled={
                                                inspection.statusId !== InspectionStatusType.InspectionDelivered ||
                                                !drawId ||
                                                drawId === 0
                                            }
                                        >
                                            Link to Draw
                                        </button>
                                    </>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td style={{ width: '90px' }}>Borrower:</td>
                            <td>
                                <b>{inspectionsData!.borrowerName}</b>
                            </td>
                        </tr>
                        <tr>
                            <td>Address:</td>
                            <td>
                                <b>
                                    {inspectionsData!.address.street1} {inspectionsData!.address.street2}
                                    {inspectionsData!.address.city}, {inspectionsData!.address.state}{' '}
                                    {inspectionsData!.address.zipCode}
                                </b>
                            </td>
                        </tr>
                        <tr>
                            <td>Requested:</td>
                            <td>
                                <b>{formatDateTime(inspection.orderedDate)}</b>
                            </td>
                        </tr>

                        <tr style={{ height: '36px' }}>
                            <td>
                                {inspection.statusId === InspectionStatusType.InspectionDelivered ? 'Delivered' : 'Last Update'}:
                            </td>
                            <td>
                                <b>
                                    {inspection.refreshedDate && formatDateTime(inspection.refreshedDate)}
                                    &nbsp;&nbsp;
                                </b>
                                {inspection.statusId !== InspectionStatusType.InspectionDelivered &&
                                    inspection.statusId !== InspectionStatusType.Error &&
                                    inspection.statusId !== InspectionStatusType.ResetRequested && (
                                        <button
                                            className='button hollow'
                                            type='button'
                                            title='Poll Granite and get latest inspection status'
                                            onClick={onRefreshStatus}
                                            // disabled={
                                            //   currentInspection.statusId === InspectionStatusType.InspectionPending ||
                                            //   currentInspection.statusId === InspectionStatusType.InspectionInProgress
                                            // }
                                            style={{ padding: '6px', margin: '0' }}
                                        >
                                            Request Granite Update
                                        </button>
                                    )}
                            </td>
                        </tr>

                        <tr>
                            <td>Percent:</td>
                            <td>
                                <b>{onPercentRender({ value: inspection.percentComplete })}</b>
                            </td>
                        </tr>

                        {inspection.statusId === InspectionStatusType.InspectionDelivered && (
                            <tr>
                                <td>Report File:</td>
                                <td>
                                    <b>
                                        <button
                                            className='link'
                                            onClick={(event) => onDownloadClick(event, inspection.reportUrl ?? '')}
                                        >
                                            {inspection.fileName}
                                        </button>
                                    </b>
                                </td>
                            </tr>
                        )}

                        {(inspectionsData!.graniteId || inspection.graniteId) && (
                            <tr style={{ height: '36px' }}>
                                <td style={{ width: '140px' }}>Granite Info:</td>
                                <td>
                                    Project Id: <b>{inspectionsData!.graniteId ?? 'Not yet created'}</b>
                                    <br />
                                    Inspection Id: <b>{inspection.graniteId ?? 'Not yet created'}</b>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InspectionDetails;
